import React, { useState, useEffect, useRef, useCallback } from 'react'

const EditableNumericInput = React.memo(({ item, initialValue, onValueChange, fieldName, step = "0.01", min = "0" }) => {
    const [localValue, setLocalValue] = useState(initialValue);
    const [isPending, setIsPending] = useState(false);
    const timeoutRef = useRef(null);

    useEffect(() => {
        setLocalValue(initialValue);
    }, [initialValue]);

    const handleChange = useCallback((e) => {
        const newValue = e.target.value;
        setLocalValue(newValue);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        setIsPending(true);

        timeoutRef.current = setTimeout(() => {
            onValueChange(item, fieldName, parseFloat(newValue) || 0);
            setIsPending(false);
        }, 1000);
    }, [item, onValueChange, fieldName]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <input
                type="number"
                value={localValue}
                onChange={handleChange}
                style={{
                    width: '80px',
                    textAlign: 'right',
                    border: isPending ? '1px solid #ffa500' : '1px solid #ccc',
                    borderRadius: '3px',
                    padding: '2px 4px',
                    fontSize: '13px',
                    backgroundColor: isPending ? '#fff8e1' : 'white',
                    transition: 'border-color 0.2s ease, background-color 0.2s ease'
                }}
                step={step}
                min={min}
            />
            {isPending && (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        right: '-20px',
                        transform: 'translateY(-50%)',
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        border: '2px solid #ffa500',
                        borderTop: '2px solid transparent',
                        animation: 'spin 1s linear infinite'
                    }}
                />
            )}
            <style>
                {`
                    @keyframes spin {
                        0% { transform: translateY(-50%) rotate(0deg); }
                        100% { transform: translateY(-50%) rotate(360deg); }
                    }
                `}
            </style>
        </div>
    );
});

const EditableSelect = React.memo(({ item, initialValue, onValueChange, fieldName, options }) => {
    const [localValue, setLocalValue] = useState(initialValue);
    const [isPending, setIsPending] = useState(false);
    const timeoutRef = useRef(null);

    useEffect(() => {
        setLocalValue(initialValue);
    }, [initialValue]);

    const handleChange = useCallback((e) => {
        const newValue = e.target.value;
        setLocalValue(newValue);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        setIsPending(true);

        timeoutRef.current = setTimeout(() => {
            onValueChange(item, fieldName, parseFloat(newValue) || 0);
            setIsPending(false);
        }, 1000);
    }, [item, onValueChange, fieldName]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <select className="form-control w-100" value={localValue} onChange={handleChange}>
                {options.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
        </div>
    );
});

const EditableAlphanumericInput = React.memo(({ item, initialValue, onValueChange, fieldName, className = '', maxLength = 50 }) => {
    const [localValue, setLocalValue] = useState(initialValue);
    const [isPending, setIsPending] = useState(false);
    const timeoutRef = useRef(null);

    useEffect(() => {
        setLocalValue(initialValue);
    }, [initialValue]);

    const handleChange = useCallback((e) => {
        const newValue = e.target.value;
        
        const alphanumericValue = newValue.replace(/[^a-zA-Z0-9]/g, '');
        
        setLocalValue(alphanumericValue);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        setIsPending(true);

        timeoutRef.current = setTimeout(() => {
            onValueChange(item, fieldName, alphanumericValue);
            setIsPending(false);
        }, 1000);
    }, [item, onValueChange, fieldName]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <input
                type="text"
                value={localValue}
                onChange={handleChange}
                maxLength={maxLength}
                className={className}
                style={{
                    width: '120px',
                    textAlign: 'left',
                    border: isPending ? '1px solid #ffa500' : '1px solid #ccc',
                    borderRadius: '3px',
                    padding: '2px 4px',
                    fontSize: '13px',
                    backgroundColor: isPending ? '#fff8e1' : 'white',
                    transition: 'border-color 0.2s ease, background-color 0.2s ease'
                }}
                placeholder="Solo letras y números"
            />
            {isPending && (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        right: '-20px',
                        transform: 'translateY(-50%)',
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        border: '2px solid #ffa500',
                        borderTop: '2px solid transparent',
                        animation: 'spin 1s linear infinite'
                    }}
                />
            )}
        </div>
    );
});

const EditableDateInput = React.memo(({ item, initialValue, onValueChange, fieldName, className = '' }) => {
    const [localValue, setLocalValue] = useState('');
    const [isPending, setIsPending] = useState(false);
    const timeoutRef = useRef(null);
    const isUserInteracting = useRef(false);
    const lastCommittedValue = useRef('');

    const formatDateForInput = (date) => {
        if (!date) return '';
        const d = new Date(date);
        if (isNaN(d.getTime())) return '';
        
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        if (isUserInteracting.current || isPending) {
            return;
        }

        const formattedInitialValue = (() => {
            if (!initialValue) return '';
            if (initialValue instanceof Date) {
                return formatDateForInput(initialValue);
            } else {
                const parsedDate = new Date(initialValue);
                return isNaN(parsedDate.getTime()) ? '' : formatDateForInput(parsedDate);
            }
        })();

        if (formattedInitialValue !== lastCommittedValue.current) {
            setLocalValue(formattedInitialValue);
            lastCommittedValue.current = formattedInitialValue;
        }
    }, [initialValue, isPending]);

    const handleChange = useCallback((e) => {
        const newValue = e.target.value;
        setLocalValue(newValue);
        isUserInteracting.current = true;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        setIsPending(true);

        timeoutRef.current = setTimeout(() => {
            if (newValue === '') {
                onValueChange(item, fieldName, null);
            } else {
                const date = new Date(newValue + 'T00:00:00');
                onValueChange(item, fieldName, date);
            }
            lastCommittedValue.current = newValue;
            setIsPending(false);
            isUserInteracting.current = false;
        }, 800);
    }, [item, onValueChange, fieldName]);

    const handleFocus = useCallback(() => {
        isUserInteracting.current = true;
    }, []);

    const handleBlur = useCallback(() => {
        setTimeout(() => {
            if (!isPending) {
                isUserInteracting.current = false;
            }
        }, 100);
    }, [isPending]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const today = new Date();
    const minDate = formatDateForInput(today);

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <input
                type="date"
                value={localValue}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                min={minDate}
                className={className}
                style={{
                    width: '140px',
                    textAlign: 'left',
                    border: isPending ? '1px solid #ffa500' : '1px solid #ccc',
                    borderRadius: '3px',
                    padding: '4px 6px',
                    fontSize: '13px',
                    backgroundColor: isPending ? '#fff8e1' : 'white',
                    transition: 'border-color 0.2s ease, background-color 0.2s ease'
                }}
            />
            
            {isPending && (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        right: '-20px',
                        transform: 'translateY(-50%)',
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        border: '2px solid #ffa500',
                        borderTop: '2px solid transparent',
                        animation: 'spin 1s linear infinite'
                    }}
                />
            )}
        </div>
    );
});

export { EditableNumericInput, EditableSelect, EditableAlphanumericInput, EditableDateInput };
